/**
 * The currency module's PURE half: how money is written as text, with no store behind it.
 *
 * 🔴 **It is a separate file for one reason.** `./shared` imports core's redux store, and importing
 * a module imports everything behind it. A consumer that needs only a formatter or the defaults
 * (`packages/ui/kiosk/theme`, which every kiosk leaf imports, or the customer display window)
 * would otherwise drag the whole store graph in, `utils/navigation`'s `createBrowserHistory()`
 * included: a `document is not defined` in any plain-node render.
 *
 * `./shared` re-exports all of this, so `@rista/core/currency` stays the one public entry. Import
 * THIS file directly (`@rista/core/currency/format`) only when you must not pull the store.
 *
 * 🔴 **Display only.** Nothing here may reach the money MATHS: `roundOffTwoDecimal`, the `round2`
 * bodies and `@rista/payments`' `money/legs.ts` decide what is charged and sent, and they stay on
 * two decimals whatever a merchant shows. Wire a display precision into them and the amount
 * charged stops matching the amount quoted.
 */

/* ── the shapes ────────────────────────────────────────────────────────────────────────────── */

/**
 * Where the commas go in a big number:
 *
 * | style         | 1,20,00,000 written as |
 * |---------------|------------------------|
 * | `"lakhs"`     | `1,20,00,000` (Indian: lakhs and crores) |
 * | `"thousands"` | `12,000,000`  (every three digits) |
 * | `"none"`      | `12000000`    (no commas) |
 */
export type CommaStyle = "lakhs" | "thousands" | "none";

/**
 * How THIS merchant's money looks: the symbol in front, the digits after the point, and where the
 * commas go. One object, resolved from one slice at one moment.
 *
 * Read it with `useMoneySettings()` in a component or `moneySettings()` anywhere else; hand it to
 * `formatMoney` / `formatAmount` where no store can be read.
 *
 * @example { symbol: "₹", decimals: 2, commaStyle: "lakhs" }
 */
export interface MoneySettings {
  /** The symbol printed in front of the digits, already decoded: `"₹"`, `"$"`, `"Lek"`. */
  symbol: string;
  /** Digits after the point. Anything unusable falls back to two (`amountDecimalsFor`). */
  decimals: number;
  /** Where the commas go. Anything unrecognised falls back to lakhs (`commaStyleFor`). */
  commaStyle: CommaStyle;
}

/**
 * What ONE line of code may change for ONE figure, and deliberately nothing else. The symbol and
 * the lakhs-or-thousands choice are the merchant's: a screen that could choose them could disagree
 * with the bill beside it. A figure may only DROP its commas.
 *
 * @example money(100, { decimals: 0 })             // "₹100"        a rate base: "per ₹100 spent"
 * @example money(120000, { commaStyle: "none" })   // "₹120000.00"  no commas for this figure
 */
export interface MoneyOptions {
  /** Digits after the point for this figure. `0` is a real answer, not "unset". */
  decimals?: number;
  /** `"none"` drops the commas for this figure. Absent keeps the merchant's style. */
  commaStyle?: "none";
}

/**
 * 🔴 **THE type of a money prop.** A function that turns a number into a complete money string.
 * Components take one of these instead of a `currencySymbol` prop, so a bare number can never be
 * pasted beside a symbol (the defect that had `₹4820` next to a bill reading `₹4820.00`).
 *
 * A screen passes `useMoney()`; a test passes `(value) => formatMoney(value, settings)`.
 */
export type MoneyFormat = (value: unknown, options?: MoneyOptions) => string;

/* ── the constants ─────────────────────────────────────────────────────────────────────────── */

/** The currency code a business falls back to when it has none: Indian rupees. */
export const INR = "INR";

/**
 * The app-side fallback symbol. ⚠️ **NOT the symbol table**: that is rista-library's `common.curr`,
 * 118 currencies deep, which `currencySymbolFor` reads first. This has one key.
 *
 * The `string | any` is transcribed verbatim from both apps' v1 copies: it collapses to `any`,
 * which their index-by-unknown-string call sites rely on.
 */
export const CURRENCY_CONSTANTS: string | any = {
  INR: "₹",
};

/** `toFixed` throws `RangeError` outside 0–100. The ceiling is the runtime's, not a product rule. */
export const MAX_AMOUNT_DECIMALS = 100;

/**
 * What every merchant gets until the backend sends their own: rupee, two decimals, lakhs.
 * Also the fallback for a host whose payload arrived without settings (the customer display).
 *
 * Frozen because it is shared: one caller mutating it would change every price on the terminal.
 */
export const DEFAULT_MONEY_SETTINGS: Readonly<MoneySettings> = Object.freeze({
  symbol: CURRENCY_CONSTANTS[INR],
  decimals: 2,
  commaStyle: "lakhs",
});

/**
 * The browser locale that writes each comma style. `"none"` has none: it is written with
 * `toFixed`. Both locales use `.` as the decimal point, so the style moves only the commas.
 */
const LOCALE_FOR_COMMA_STYLE: Record<Exclude<CommaStyle, "none">, string> = {
  lakhs: "en-IN",
  thousands: "en-US",
};

/* ── the formatters ────────────────────────────────────────────────────────────────────────── */

/**
 * The precision to use, given whatever was configured or asked for.
 *
 * 🔴 **A trust boundary, not a tidy-up.** `toFixed` throws outside 0–100, so one bad value off the
 * wire would blank every price on a terminal at once. Nothing reaches `toFixed` without passing here.
 *
 * 🔴 **`0` is a legitimate answer** (a whole-unit currency), hence an explicit integer check and
 * never `configured || 2`, which turns a configured zero into two.
 *
 * @example amountDecimalsFor(3)   // 3
 * @example amountDecimalsFor(0)   // 0
 * @example amountDecimalsFor(-1)  // 2, the default
 */
export function amountDecimalsFor(configured?: number | null): number {
  const usable =
    typeof configured === "number" &&
    Number.isInteger(configured) &&
    configured >= 0 &&
    configured <= MAX_AMOUNT_DECIMALS;
  return usable ? configured : DEFAULT_MONEY_SETTINGS.decimals;
}

/**
 * The comma style to use, given whatever was configured or sent.
 *
 * 🔴 **A trust boundary, like `amountDecimalsFor`.** The style can arrive over the wire (the
 * customer display's payload) or, one day, from the backend; a value this module does not know
 * writes the default rather than silently dropping every comma.
 *
 * @example commaStyleFor("thousands") // "thousands"
 * @example commaStyleFor("en-IN")     // "lakhs", the default
 */
export function commaStyleFor(configured?: unknown): CommaStyle {
  return configured === "lakhs" || configured === "thousands" || configured === "none"
    ? configured
    : DEFAULT_MONEY_SETTINGS.commaStyle;
}

/**
 * An amount's digits, no symbol, written the way `settings` say, with this call's `options` applied.
 *
 * Takes `unknown` on purpose: sale documents carry nulls and app prices are sometimes strings, so
 * anything unparseable answers zero at the right precision.
 *
 * 🔴 **A `NaN` therefore renders as zero, and that silence is a hazard: guard it UPSTREAM.** A
 * broken calculation reaching a screen as a confident `₹0.00` is worse than an obvious error;
 * check `Number.isFinite` where the amount is COMPUTED (`@rista/payments`' `money/calculate.ts`).
 *
 * 🔴 **Its own output is not valid input.** `Number("1,200.00")` is `NaN`, so formatting a
 * formatted string answers `"0.00"`. Keep numbers in state; format once, where they are drawn.
 *
 * @example formatAmount(1234567.5, { decimals: 2, commaStyle: "lakhs" })                          // "12,34,567.50"
 * @example formatAmount(1234567.5, { decimals: 2, commaStyle: "thousands" })                      // "1,234,567.50"
 * @example formatAmount(1234567.5, { decimals: 2, commaStyle: "lakhs" }, { commaStyle: "none" })  // "1234567.50"
 */
export function formatAmount(
  value: unknown,
  settings: Pick<MoneySettings, "decimals" | "commaStyle">,
  options?: MoneyOptions,
): string {
  // `??`, never `||`: a per-call 0 is a real answer.
  const digits = amountDecimalsFor(options?.decimals ?? settings.decimals);
  const commaStyle = options?.commaStyle ?? commaStyleFor(settings.commaStyle);
  const locale = commaStyle === "none" ? undefined : LOCALE_FOR_COMMA_STYLE[commaStyle];
  const numeric = Number(value);
  // One renderer for the value AND the negative-zero probe below, so the two can never disagree
  // about what "zero at this precision" looks like in this locale.
  const render = (number: number) =>
    locale
      ? number.toLocaleString(locale, {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        })
      : number.toFixed(digits);
  const rendered = render(Number.isFinite(numeric) ? numeric : 0);
  // "-0.00" is a lie: a value too small to show is nothing, not minus nothing. Rounding cannot
  // avoid it, since -0.004 becomes a zero that keeps its sign.
  return rendered === `-${render(0)}` ? rendered.slice(1) : rendered;
}

/**
 * Symbol and digits together, with no store: for tests, the kiosk's leaves, the customer display
 * window, anything pure. Everywhere else, `useMoney()` or `money()` resolves `settings` for you.
 *
 * 🔴 A NEGATIVE signs the WHOLE thing: `-₹5.50`, never `₹-5.50`. Decided here so no caller has to.
 * A caller drawing its own `-` passes `Math.abs` (the kiosk's `BillSummarySection` does).
 *
 * @example formatMoney(1234.5, { symbol: "₹", decimals: 2, commaStyle: "lakhs" })  // "₹1,234.50"
 * @example formatMoney(-5.5, { symbol: "₹", decimals: 2, commaStyle: "lakhs" })    // "-₹5.50"
 */
export function formatMoney(value: unknown, settings: MoneySettings, options?: MoneyOptions): string {
  const digits = formatAmount(value, settings, options);
  // ⚠️ Assumes an ASCII hyphen, which both comma styles' locales (`en-IN`, `en-US`) use. A style
  // whose locale signs with U+2212 would need this widened.
  return digits.startsWith("-") ? `-${settings.symbol}${digits.slice(1)}` : `${settings.symbol}${digits}`;
}
